import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import StepperHeader from '@/components/StepperHeader';
import Card from '@/components/Card';
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { TextInput, RadioButton, WhatsAppInput, TextArea, Checkbox } from '@/components/Inputs';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Popover from '@/components/Popover';
import { useOrganizationCustomFields, usePeopleCustomFields } from '@/api';

const WIDGET_SCRIPT = 'https://d1270hzxcdv0dz.cloudfront.net/widget-whatsapp-v1.0.js';

const HEADER_LABELS = [
  'Config. Widget',
  'Config. WhatsApp',
  'Campos Formulário',
  'Gerar Código',
  'Concluído!'
];

const fieldOptions = [
  {
    label: 'Nome',
    name: 'name',
    inputTitle: 'Título do campo nome',
    required: true,
    type: 'text',
    customField: false
  },
  {
    label: 'Email',
    name: 'email',
    inputTitle: 'Título do campo email',
    required: true,
    type: 'email',
    customField: false
  },
  {
    label: 'Whatsapp',
    name: 'whatsapp',
    inputTitle: 'Título do campo whatsapp',
    required: true,
    type: 'text',
    customField: false
  }
];

const STEPS = {
  CONFIG_WIDGET: 0,
  CONFIG_WHATSAPP: 1,
  FORM_FIELDS: 2,
  GENERATE_SCRIPT: 3,
  FINISHED: 4
};

const TIMEOUT = 3000;
const MAX_SELECTED_FIELDS = 6;

WebsiteIntegrationModal.propTypes = {
  user: PropTypes.object
};

WebsiteIntegrationModal.defaultProps = {
  user: {}
};

function WebsiteIntegrationModal({ user }) {
  const [step, setStep] = useState(STEPS.CONFIG_WIDGET);
  const [createEntity, setCreateEntity] = useState('organization');
  const [createDeal, setCreateDeal] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('Olá, gostaria de falar com um atendente!');
  const [generatedScript, setGeneratedScript] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [selectedFields, setSelectedFields] = useState(fieldOptions.map((option) => option.name));
  const [fieldRequired, setFieldRequired] = useState(
    Object.fromEntries(fieldOptions.map((option) => [option.name, option.required]))
  );

  const {
    data: organizationCustomFields,
    isSuccess: loadedOrganizationCustomFields
  } = useOrganizationCustomFields();

  const {
    data: peopleCustomFields,
    isSuccess: loadedPeopleCustomFields
  } = usePeopleCustomFields();

  const toggleFieldRequired = (fieldName) => (value) => {
    setFieldRequired((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const getDynamicFieldOptions = () => {
    let customFields = [];

    if (createEntity === 'organization' && loadedOrganizationCustomFields) {
      customFields = organizationCustomFields?.data || [];
    } else if (createEntity === 'person' && loadedPeopleCustomFields) {
      customFields = peopleCustomFields?.data || [];
    }

    const mappedCustomFields = customFields
      .filter((field) => ['text', 'select', 'decimal', 'integer'].includes(field.type))
      .map((field) => ({
        label: field.name,
        name: field.name,
        identifier: field.identifier,
        inputTitle: `Título do campo ${field.name}`,
        type: field.type,
        required: true,
        customField: true,
        customFieldOptions: field.customFieldOptions
      }));

    return [...fieldOptions, ...mappedCustomFields];
  };

  const [fieldValues, setFieldValues] = useState(() => Object
    .fromEntries(getDynamicFieldOptions().map((option) => [option.name, option.label])));

  useEffect(() => {
    const allOptions = getDynamicFieldOptions();
    const initialValues = Object.fromEntries(allOptions
      .map((option) => [option.name, option.label]));
    setFieldValues(initialValues);
  }, [organizationCustomFields, peopleCustomFields, createEntity]);

  const handleFieldToggle = (fieldName) => (isChecked) => {
    setSelectedFields((prev) => (
      isChecked ? [...prev, fieldName] : prev.filter((name) => name !== fieldName)));
  };

  const handleFieldValueChange = (fieldName) => (value) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleGenerateScript = () => {
    const config = {
      userId: user?.id,
      companyName: organizationName,
      whatsappNumber,
      whatsappMessage: welcomeMessage,
      entity: createEntity,
      deal: createDeal,
      fields: selectedFields.map((fieldName) => {
        const fieldOption = getDynamicFieldOptions().find((option) => option.name === fieldName);
        return {
          name: fieldOption.name,
          identifier: fieldOption.identifier,
          label: fieldValues[fieldName],
          type: fieldOption.type,
          required: fieldRequired[fieldName],
          customField: fieldOption.customField,
          customFieldOptions: fieldOption.customFieldOptions
        };
      })
    };

    const scriptContent = `
      <script>
        window.WidgetWhatsConfig = ${JSON.stringify(config, null, 2)};
      </script>
      <script src="${WIDGET_SCRIPT}" defer></script>
    `.trim();

    setGeneratedScript(scriptContent);
    setStep(STEPS.FINISHED);
  };

  const handleOnCopy = () => {
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, TIMEOUT);
  };

  const renderStepContent = () => {
    switch (step) {
      case STEPS.CONFIG_WIDGET:
        return {
          isNextDisabled: !organizationName,
          content: (
            <>
              <span className='text-dark fw-bold'>Nome da sua empresa</span>
              <TextInput
                onChange={setOrganizationName}
                value={organizationName}
                name='name'
                className='mb-0'
                placeholder='Digite o nome da sua empresa'
                autoComplete='off'
                variant='white'
                autoFocus
              />
              <div className='mb-2 mt-4'>
                <span className='fw-bold text-dark'>Os seus leads são empresas ou pessoas?</span>
                <RadioButton
                  id='entity_empresa'
                  checked={createEntity === 'organization'}
                  name='create_entity'
                  label='Empresas'
                  className='me-2 my-1'
                  noFlex
                  value='Empresas'
                  onChange={() => setCreateEntity('organization')}
                  checkClassName='mb-0 cursor-pointer'
                />
                <RadioButton
                  id='entity_pessoa'
                  checked={createEntity === 'person'}
                  name='create_entity'
                  label='Pessoas'
                  className='me-2 my-1'
                  noFlex
                  value='Pessoas'
                  onChange={() => setCreateEntity('person')}
                  checkClassName='mb-0 cursor-pointer'
                />
              </div>
              <div className='my-2'>
                <span className='fw-bold text-dark'>Criar negociação para o Lead?</span>
                <RadioButton
                  id='create_deal'
                  checked={createDeal}
                  name='create_deal'
                  onChange={() => setCreateDeal(true)}
                  label='Sim, criar negociação'
                  className='me-2 my-1'
                  noFlex
                  checkClassName='mb-0 cursor-pointer'
                />
                <RadioButton
                  id='not_create_deal'
                  checked={!createDeal}
                  name='create_deal'
                  onChange={() => setCreateDeal(false)}
                  label='Não, não criar negociação'
                  className='me-2 my-1'
                  noFlex
                  checkClassName='mb-0 cursor-pointer'
                />
              </div>
            </>
          )
        };

      case STEPS.CONFIG_WHATSAPP:
        return {
          isNextDisabled: !whatsappNumber,
          content: (
            <>
              <div>
                <span className='text-dark fw-bold'>Número do WhatsApp</span>
                <WhatsAppInput
                  name='whatsapp_number'
                  variant='white'
                  value={whatsappNumber}
                  onChange={setWhatsappNumber}
                />
                <div className='text-muted text-small mt-n3'>
                  Inclua o código do país e DDD (ex: 5511999999999)
                </div>
              </div>
              <span className='text-dark fw-bold mt-3 d-block'>Mensagem de boas-vindas</span>
              <TextInput
                name='whatsapp_welcome'
                value={welcomeMessage}
                placeholder='Olá! Como podemos te ajudar?'
                variant='white'
                onChange={setWelcomeMessage}
              />
            </>
          )
        };

      case STEPS.FORM_FIELDS:
        return {
          isNextDisabled: !fieldValues.name || fieldValues.name.trim() === '',
          content: (
            <div className='text-dark'>
              <h3 className='d-flex align-items-start'>Campos do formulário</h3>
              <div className='mb-3 mt-5'>
                Selecione quais campos você deseja incluir no seu formulário (máximo 6):
              </div>
              <Popover
                placement='bottom-start'
                contentClassName='p-0 rounded'
                trigger={['click']}
                className='width-5 max-height-5 overflow-y-auto'
                rootClose
                rootCloseEvent='mousedown'
                flip
                content={() => (
                  <div className='p-3'>
                    {getDynamicFieldOptions().map((fieldOption) => (
                      <Checkbox
                        key={fieldOption.name}
                        label={fieldOption.label}
                        name={fieldOption.name}
                        className='ms-0 mb-2 text-dark-gray fw-bold'
                        onChange={handleFieldToggle(fieldOption.name)}
                        checked={selectedFields.includes(fieldOption.name)}
                        disabled={
                          fieldOption.name === 'name' ||
                          (
                            selectedFields.length >= MAX_SELECTED_FIELDS &&
                            !selectedFields.includes(fieldOption.name)
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              >
                <Button>
                  <Icon name='plus' className='me-1' />
                  Adicionar campos
                  <Icon name='arrow-down' className='ms-1' />
                </Button>
              </Popover>

              <div className='fw-bold text-dark mt-5 mb-3'>Campos Selecionados:</div>
              {selectedFields.map((fieldName) => {
                const fieldOption = getDynamicFieldOptions()
                  .find((option) => option.name === fieldName);
                return (
                  <Card key={fieldName} className='mt-3 px-3 py-2 border-light'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-bold text-dark mb-2 d-block'>
                        {fieldOption?.inputTitle}
                      </span>
                      {
                        fieldOption.name !== 'name' && (
                          <Button
                            size='sm'
                            variant='white'
                            onClick={() => handleFieldToggle(fieldName)(false)}
                          >
                            <Icon name='close' className='mb-2' />
                          </Button>
                        )
                      }
                    </div>
                    <TextInput
                      name={fieldName}
                      value={fieldValues[fieldName]}
                      onChange={handleFieldValueChange(fieldName)}
                      placeholder={`Digite o label de ${fieldOption?.label.toLowerCase()}`}
                      variant='white'
                    />
                    {
                      fieldOption.name !== 'name' && (
                        <Checkbox
                          label='Obrigatório'
                          name={`required_${fieldName}`}
                          className='ms-0 mb-2 text-dark-gray fw-bold'
                          onChange={(value) => toggleFieldRequired(fieldName)(value)}
                          checked={fieldRequired[fieldName]}
                        />
                      )
                    }
                  </Card>
                );
              })}
            </div>
          )
        };

      case STEPS.GENERATE_SCRIPT:
        return {
          isNextDisabled: false,
          content: (
            <div>
              <h3 className='text-dark d-flex align-items-center justify-content-center'>
                Revise as configurações do seu Widget
              </h3>
              <div className='d-flex justify-content-between'>
                <div>
                  <div className='text-dark justify-content-start my-2'>
                    <div className='text-dark fw-bold'>
                      Nome da sua empresa
                    </div>
                    <div className='mt-1 text-dark'>{organizationName}</div>
                  </div>
                  <div className='text-dark justify-content-start my-4'>
                    <div className='text-dark fw-bold'>
                      Criar Negociação
                    </div>
                    <div className='mt-1 text-dark'>{createDeal ? 'Sim' : 'Não'}</div>
                  </div>
                  <div className='text-dark justify-content-start my-4'>
                    <div className='text-dark fw-bold'>
                      Campos do Formulário
                    </div>
                    <div>
                      {selectedFields.map((fieldName) => {
                        const fieldOption = getDynamicFieldOptions()
                          .find((option) => option.name === fieldName);
                        return (
                          <div key={fieldName}>
                            <div className='mt-1 text-dark'>
                              <span className='text-dark fw-bold'>
                                {`${fieldOption?.label}:`}
                              </span> {fieldValues[fieldName] || 'Não preenchido'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className='text-dark justify-content-start mt-4 mb-2'>
                    <div className='text-dark fw-bold'>
                      Tipo de Lead
                    </div>
                    <div className='mt-1 text-dark'>
                      {createEntity === 'organization' ? 'Empresas' : 'Pessoas'}
                    </div>
                  </div>
                  <div className='text-dark justify-content-start my-2'>
                    <div className='text-dark fw-bold'>
                      Número do WhatsApp
                    </div>
                    <div className='mt-1 text-dark'>{whatsappNumber}</div>
                  </div>
                  <div className='text-dark justify-content-start mt-4'>
                    <div className='text-dark fw-bold'>
                      Responsável pelos leads
                    </div>
                    <div className='mt-1 text-dark'>{user?.name}</div>
                  </div>
                </div>
              </div>
              <div className='d-flex justify-content-center align-items-center'>
                <Button
                  className='mb-2'
                  onClick={() => handleGenerateScript()}
                >
                  Gerar Script
                </Button>
              </div>
            </div>
          )
        };

      case STEPS.FINISHED:
        return {
          isNextDisabled: false,
          content: (
            <div className='text-center'>
              <h3 className='text-success my-3 d-flex justify-content-center'>
                Seu script para capturar leads está pronto!
              </h3>
              <div className='text-dark mb-2 d-flex justify-content-center'>
                Copie o código abaixo e cole-o no HTML
                do seu site, logo antes da tag de fechamento
              </div>
              <div className='position-relative'>
                <TextArea
                  name='script'
                  className='mb-0 w-100'
                  value={generatedScript}
                  readOnly
                  rows={10}
                  minRows={10}
                  maxRows={15}
                  style={{ resize: 'none' }}
                />
              </div>
              <div className='d-flex justify-content-between'>
                <div>
                  <Button
                    className='my-2'
                    onClick={() => setStep(STEPS.CONFIG_WIDGET)}
                    variant='light'
                  >
                    Gerar outro script
                  </Button>
                </div>
                <div>
                  <CopyToClipboard text={generatedScript} onCopy={handleOnCopy}>
                    <Button variant='primary' className='my-2'>
                      <Icon size='sm' className='me-1' name={isCopied ? 'check' : 'copy'} />
                      {isCopied ? 'Copiado!' : 'Copiar código'}
                    </Button>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          )
        };

      default:
        return { isNextDisabled: true, content: null };
    }
  };

  const { content, isNextDisabled } = renderStepContent();

  return (
    <div>
      <div>
        <StepperHeader
          headerClassName='d-flex justify-content-center align-items-center fw-bold'
          currentStep={step}
          headerLabels={HEADER_LABELS}
        />
        <Card className='px-4 pt-4 mb-3 pb-1 my-4 border-light'>
          <div className='my-2'>{content}</div>
        </Card>
        <div className='d-flex justify-content-between'>
          {
            step === STEPS.CONFIG_WIDGET || step === STEPS.FINISHED
              ? <div></div>
              : (
                <Button
                  variant='light'
                  onClick={() => setStep((prev) => Math.max(prev - 1, STEPS.CONFIG_WIDGET))}
                >
                  Anterior
                </Button>
              )
          }
          {![STEPS.FINISHED, STEPS.GENERATE_SCRIPT].includes(step) && (
            isNextDisabled && step === STEPS.FORM_FIELDS
              ? (
                <Tooltip content='Rótulo do campo nome é obrigatório'>
                  <div>
                    <Button
                      onClick={() => setStep((prev) => Math.min(prev + 1, STEPS.FINISHED))}
                      disabled={isNextDisabled}
                    >
                      Próximo
                    </Button>
                  </div>
                </Tooltip>
              )
              : (
                <Button
                  onClick={() => setStep((prev) => Math.min(prev + 1, STEPS.FINISHED))}
                  disabled={isNextDisabled}
                >
                  Próximo
                </Button>
              )
          )}

        </div>
      </div>
    </div>
  );
}

export default WebsiteIntegrationModal;
